import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ButtonClassNames, InputRegexPattern, LocalStorageItems, ModalVariants } from '../../../../../../models/Common';
import { CustomMUIButton } from '../../../../buttons/CustomMUIButton';
import { FormInputText } from '../../../../../inputs/FormInputText';
import { ApiError } from '../../../../../../models/ApiError';
import { useAppDispatch } from '../../../../../../hooks/redux';
import { useCustomSnackbar } from '../../../../../../hooks/useCustomEnqueueSnackbar';
import { userInfoApi } from '../../../../../../services/apiServices/user/userInfo';
import { modalSlice } from '../../../../../../store/reducers/ModalSlice';
import { FormInputPassword } from '../../../../../inputs/FormInputPassword';
import { getErrorMessage } from '../../../../../../utils';

interface IEditEmailForm {
  editedEmail: string;
  currentPassword: string;
}

export const EditEmail: FC = () => {
  const { t } = useTranslation();
  const methods = useForm<IEditEmailForm>();
  const { handleSubmit, setError } = methods;
  const [editEmailApi, { isLoading }] = userInfoApi.useEditEmailMutation();
  const { openModal } = modalSlice.actions;
  const dispatch = useAppDispatch();
  const { customEnqueueSnackbar } = useCustomSnackbar();

  const handleEditEmail = async ({ editedEmail, currentPassword }: IEditEmailForm) => {
    try {
      await editEmailApi({ editedEmail, currentPassword })
        .unwrap()
        .then(() => {
          dispatch(
            openModal({
              modalVariants: ModalVariants.Success,
              content: {
                title: `${t('input.newEmail')}!`,
                description: `${t('accountSettings.confirmationMessageHasBeenSentToMail')} - `,
                boldDescription: editedEmail,
              },
            }),
          );
          localStorage.setItem(LocalStorageItems.Email, editedEmail);
        });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof IEditEmailForm, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof IEditEmailForm>;
        apiErrorsKeys.forEach((element) => {
          setError(element, {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('accountSettings.editingEmail')}</article>
        <p className="modals__header__description">{t('accountSettings.enterNewEmailAddress')}</p>
      </div>
      <form className="modals__form" onSubmit={handleSubmit(handleEditEmail)}>
        <FormProvider {...methods}>
          <FormInputPassword
            name="currentPassword"
            className="primary"
            label={t('auth.enterPassLk')}
            isRequired
            minLength={6}
            maxLength={16}
            autoComplete="new-password"
            fieldHasName
          />
          <FormInputText
            name="editedEmail"
            className="primary"
            label={t('input.newEmail')}
            isRequired
            minLength={4}
            maxLength={99}
            autoComplete="new-password"
            pattern={InputRegexPattern.Email}
            fieldHasName
          />
          <div className="modals__btns-wrapper">
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('buttons.save')}
                type="submit"
                className={ButtonClassNames.PrimaryFulfilled}
                isBold
                isLoading={isLoading}
              />
            </div>
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('auth.forgotMyPassword')}
                handleClick={handleForgotPassword}
                className={ButtonClassNames.SecondaryOutlined}
                isBold
                type="button"
              />
            </div>
          </div>
        </FormProvider>
      </form>
    </div>
  );
};
